import { useEffect, useRef, useState, type ComponentType } from "react";
import {
  Briefcase,
  Building2,
  Cake,
  ChevronDown,
  ChevronUp,
  Globe,
  HeartHandshake,
  Home,
  IdCard,
  Mail,
  Map as MapIcon,
  MapPin,
  MapPinned,
  MessageCircle,
  Phone,
  Smartphone,
  Sparkles,
  StickyNote,
  Tags,
  User,
  UserCircle2,
} from "lucide-react";
import { BRAZILIAN_STATES, type CampoConfiguravel } from "@bookdental/schemas";
import { fetchAddressFromCep } from "@/lib/viacep";
import { titleCasePtBR } from "@/lib/name-case";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/cn";
import { useConfiguracaoDePacientes } from "@/lib/use-configuracao-de-pacientes";
import { BirthdayPicker } from "./BirthdayPicker";
import { digitosDeCpf, mascaraDeCpf } from "@/lib/documento";
import { PatientTagInput } from "./PatientTagInput";
import { PatientAvatarUpload } from "./PatientAvatarUpload";
import { PhoneInputWithCountry } from "./PhoneInputWithCountry";

export type PatientFormValues = {
  name: string;
  identifier: string;
  clinicalRecordNumber: string;
  document: string;
  sex: "M" | "F" | "OTHER" | "";
  birthDate: string; // ISO date yyyy-mm-dd
  phone: string;
  landline: string;
  whatsapp: string;
  contatoDeEmergenciaNome: string;
  contatoDeEmergenciaTelefone: string;
  email: string;
  zipCode: string;
  address: string;
  addressNumber: string;
  addressComplement: string;
  neighborhood: string;
  city: string;
  state: string;
  profession: string;
  motherName: string;
  insurancePlanName: string;
  insurancePlanNumber: string;
  avatarUrl: string | null;
  tags: string[];
  howDidMeet: string;
  notes: string;
  active: boolean;
  /** Estrangeiro, sem CPF: o documento é texto livre e o CPF obrigatório não vale. */
  estrangeiro: boolean;
};

export const EMPTY_PATIENT_FORM: PatientFormValues = {
  name: "",
  identifier: "",
  clinicalRecordNumber: "",
  document: "",
  sex: "",
  birthDate: "",
  phone: "",
  landline: "",
  whatsapp: "",
  contatoDeEmergenciaNome: "",
  contatoDeEmergenciaTelefone: "",
  email: "",
  zipCode: "",
  address: "",
  addressNumber: "",
  addressComplement: "",
  neighborhood: "",
  city: "",
  state: "",
  profession: "",
  motherName: "",
  insurancePlanName: "",
  insurancePlanNumber: "",
  avatarUrl: null,
  tags: [],
  howDidMeet: "",
  notes: "",
  active: true,
  estrangeiro: false,
};

const TAG_PRESETS = [
  "VIP",
  "Alérgico",
  "Diabético",
  "Gestante",
  "Convênio",
  "Ortodontia",
  "Ansioso",
  "Indicação",
];

const HOW_DID_MEET_OPTIONS = [
  "Google",
  "Instagram",
  "Facebook",
  "Indicação de paciente",
  "Indicação de profissional",
  "Convênio",
  "Passando em frente",
  "Outro",
];

type IconComp = ComponentType<{ className?: string }>;

function formatPhone(v: string): string {
  const d = v.replace(/\D/g, "").slice(0, 11);
  if (d.length <= 2) return d ? `(${d}` : "";
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
}
function formatCep(v: string): string {
  const d = v.replace(/\D/g, "").slice(0, 8);
  if (d.length <= 5) return d;
  return `${d.slice(0, 5)}-${d.slice(5)}`;
}

type Props = {
  values: PatientFormValues;
  onChange: (partial: Partial<PatientFormValues>) => void;
};

export function PatientForm({ values, onChange }: Props) {
  const tagsQuery = trpc.patient.listTags.useQuery();
  const [cepStatus, setCepStatus] = useState<"idle" | "searching" | "not_found">("idle");
  const isForeigner = values.estrangeiro;
  // Configurações › Pacientes: oculto some, obrigatório ganha "*". Sem nada configurado, tudo
  // opcional, como sempre foi.
  const campos = useConfiguracaoDePacientes().cadastro.campos;
  const ver = (c: CampoConfiguravel) => campos[c] !== "oculto";
  const rotulo = (texto: string, c: CampoConfiguravel) => (campos[c] === "obrigatorio" ? `${texto} *` : texto);
  // Obrigatório fora do bloco rápido: o cadastro já abre completo, pra ninguém procurar o que falta.
  const exigeAlemDoRapido = (Object.keys(campos) as CampoConfiguravel[]).some(
    (c) => campos[c] === "obrigatorio" && !["phone", "birthDate", "motherName", "convenio"].includes(c),
  );
  // Modo inicial compacto — apenas campos essenciais. Botão "Cadastro
  // completo" revela as demais seções (e outro botão retrai de volta).
  const [showFull, setShowFull] = useState(false);
  useEffect(() => {
    if (exigeAlemDoRapido) setShowFull(true);
  }, [exigeAlemDoRapido]);
  // País do celular — default Brasil. Mudar depois pra persistir no Patient.
  const [phoneCountry, setPhoneCountry] = useState<string>("BR");
  const nameInputRef = useRef<HTMLInputElement>(null);

  // ViaCEP auto-lookup
  useEffect(() => {
    const d = values.zipCode.replace(/\D/g, "");
    if (d.length !== 8) {
      setCepStatus("idle");
      return;
    }
    setCepStatus("searching");
    fetchAddressFromCep(d).then((r) => {
      if (!r) {
        setCepStatus("not_found");
        return;
      }
      setCepStatus("idle");
      // Sempre sobrescreve ao mudar o CEP — usuário pode editar depois.
      onChange({
        address: r.address || values.address,
        neighborhood: r.neighborhood || values.neighborhood,
        city: r.city || values.city,
        state: r.state || values.state,
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.zipCode]);

  return (
    <div className="space-y-5">
      {/* ─── Bloco compacto inicial ─────────────────────────── */}
      <section>
        <div className="flex items-start gap-4">
          <PatientAvatarUpload
            value={values.avatarUrl}
            name={values.name}
            onChange={(v) => onChange({ avatarUrl: v })}
          />
          <div className="min-w-0 flex-1 space-y-2">
            <Field
              icon={User}
              label="Nome completo *"
              hint="formatação automática — escreva sem se preocupar com maiúsculas"
            >
              <input
                ref={nameInputRef}
                value={values.name}
                onChange={(e) => {
                  const raw = e.target.value;
                  const formatted = titleCasePtBR(raw);
                  const pos = e.target.selectionStart;
                  onChange({ name: formatted });
                  if (pos != null) {
                    requestAnimationFrame(() => {
                      nameInputRef.current?.setSelectionRange(pos, pos);
                    });
                  }
                }}
                onBlur={(e) => {
                  const cleaned = e.target.value.replace(/\s+/g, " ").trim();
                  if (cleaned !== values.name) onChange({ name: cleaned });
                }}
                minLength={2}
                required
                autoFocus
                autoCapitalize="words"
                autoComplete="name"
                placeholder="Ex.: Maria da Silva"
                className={cn(inputClass, "max-w-md")}
              />
            </Field>
            {ver("phone") && (
            <Field icon={Smartphone} label={rotulo("Celular", "phone")}>
              <PhoneInputWithCountry
                value={values.phone}
                onChange={(d) => onChange({ phone: d })}
                countryCode={phoneCountry}
                onCountryChange={setPhoneCountry}
                className="max-w-[280px]"
              />
            </Field>
            )}
            {ver("birthDate") && (
            <Field icon={Cake} label={rotulo("Nascimento", "birthDate")}>
              <BirthdayPicker
                value={values.birthDate}
                onChange={(ymd) => onChange({ birthDate: ymd })}
                className="max-w-[240px]"
              />
            </Field>
            )}
            {/* Nome da mãe e convênio não entram no cadastro rápido — só quando a clínica exige. */}
            {campos.motherName === "obrigatorio" && (
              <Field icon={User} label="Nome da mãe *">
                <input value={values.motherName} onChange={(e) => onChange({ motherName: e.target.value })} maxLength={120} className={cn(inputClass, "max-w-md")} />
              </Field>
            )}
            {campos.convenio === "obrigatorio" && (
              <div className="grid max-w-md grid-cols-12 gap-2">
                <Field icon={HeartHandshake} label="Convênio *" span="col-span-12 sm:col-span-7">
                  <input value={values.insurancePlanName} onChange={(e) => onChange({ insurancePlanName: e.target.value })} maxLength={120} className={inputClass} />
                </Field>
                <Field label="Carteirinha" span="col-span-12 sm:col-span-5">
                  <input value={values.insurancePlanNumber} onChange={(e) => onChange({ insurancePlanNumber: e.target.value })} maxLength={50} className={inputClass} />
                </Field>
              </div>
            )}
          </div>
        </div>

        <button
          type="button"
          onClick={() => setShowFull((v) => !v)}
          className={cn(
            "mt-4 inline-flex w-full items-center justify-center gap-1.5 rounded-md border border-dashed px-3 py-2 text-[12px] font-semibold transition-all",
            showFull
              ? "border-zinc-300 text-zinc-600 hover:border-zinc-400 hover:bg-zinc-50 dark:border-border dark:text-muted-foreground dark:hover:bg-muted"
              : "border-zinc-300 text-zinc-600 hover:border-brand-400 hover:bg-brand-50 hover:text-brand-700 dark:border-border dark:text-muted-foreground dark:hover:border-primary/40 dark:hover:bg-primary/10 dark:hover:text-primary",
          )}
        >
          {showFull ? (
            <>
              <ChevronUp className="h-3.5 w-3.5" />
              Reduzir para cadastro rápido
            </>
          ) : (
            <>
              <ChevronDown className="h-3.5 w-3.5" />
              Cadastro completo
              <span className="text-[10px] font-medium opacity-70">
                (CPF, endereço, tags…)
              </span>
            </>
          )}
        </button>
      </section>

      {/* ─── Seções completas (renderizadas só com showFull) ──── */}
      {showFull && (
        <>
          {/* ─── Identidade + Documento ───────────────────────── */}
          {(ver("sex") || ver("document")) && (
          <Section icon={isForeigner ? Globe : IdCard} title="Documento e identidade" accent="#2457ff">
            <div className="grid grid-cols-12 gap-2">
              {ver("sex") && (
              <Field icon={UserCircle2} label={rotulo("Sexo", "sex")} span="col-span-12 sm:col-span-4">
                <select
                  value={values.sex}
                  onChange={(e) =>
                    onChange({ sex: e.target.value as PatientFormValues["sex"] })
                  }
                  className={cn(inputClass, "cursor-pointer")}
                >
                  <option value="">—</option>
                  <option value="F">Feminino</option>
                  <option value="M">Masculino</option>
                  <option value="OTHER">Outro</option>
                </select>
              </Field>
              )}
              {ver("document") && (
              <>
              <Field
                icon={isForeigner ? Globe : IdCard}
                label={isForeigner ? "Documento" : rotulo("CPF", "document")}
                span="col-span-12 sm:col-span-8"
              >
                <input
                  value={isForeigner ? values.document : mascaraDeCpf(values.document)}
                  onChange={(e) =>
                    onChange({
                      document: isForeigner
                        ? e.target.value
                        : // `slice(0, 11)` no lugar de só tirar a pontuação: sem teto dava pra
                          // digitar o 12º dígito, e aí o campo mostrava uma coisa e mandava outra.
                          digitosDeCpf(e.target.value),
                    })
                  }
                  placeholder={
                    isForeigner ? "Passaporte, RNE, outro..." : "000.000.000-00"
                  }
                  inputMode={isForeigner ? "text" : "numeric"}
                  className={inputClass}
                />
                {!isForeigner && values.document.length > 0 && values.document.length < 11 && (
                  <p className="mt-1 px-1 text-[11px] text-amber-600 dark:text-amber-500">
                    CPF incompleto — faltam {11 - values.document.length}{" "}
                    {11 - values.document.length === 1 ? "dígito" : "dígitos"}.
                  </p>
                )}
              </Field>
              <label className="col-span-12 flex cursor-pointer items-center gap-2 px-1 text-[12px] text-zinc-600 select-none dark:text-muted-foreground">
                <input
                  type="checkbox"
                  checked={isForeigner}
                  onChange={(e) => {
                    onChange({ estrangeiro: e.target.checked });
                    // Limpa ao marcar e SANEIA ao desmarcar: o documento de estrangeiro é texto
                    // livre, e voltando pro modo CPF ele ficava lá inteiro — com o campo exibindo
                    // uma máscara bonita por cima de um valor que o servidor recusava.
                    onChange({ document: e.target.checked ? "" : digitosDeCpf(values.document) });
                  }}
                  className="h-3.5 w-3.5 accent-brand-600"
                />
                <span>Estrangeiro — não tem CPF</span>
              </label>
              </>
              )}
            </div>
          </Section>
          )}

          {/* ─── Contato extra ────────────────────────────────── */}
          {(ver("landline") || ver("email") || ver("whatsapp") || ver("contatoDeEmergencia")) && (
          <Section icon={Mail} title="Contato adicional" accent="#06b6d4">
            <div className="grid grid-cols-12 gap-2">
              {ver("landline") && (
              <Field icon={Phone} label={rotulo("Telefone fixo", "landline")} span="col-span-12 sm:col-span-5">
                <input
                  value={formatPhone(values.landline)}
                  onChange={(e) =>
                    onChange({ landline: e.target.value.replace(/\D/g, "").slice(0, 11) })
                  }
                  placeholder="(11) 3456-7890"
                  inputMode="tel"
                  className={inputClass}
                />
              </Field>
              )}
              {ver("email") && (
              <Field icon={Mail} label={rotulo("E-mail", "email")} span="col-span-12 sm:col-span-7">
                <input
                  type="email"
                  value={values.email}
                  onChange={(e) => onChange({ email: e.target.value })}
                  placeholder="maria@exemplo.com"
                  className={inputClass}
                />
              </Field>
              )}
              {ver("whatsapp") && (
              <Field icon={MessageCircle} label={rotulo("WhatsApp", "whatsapp")} span="col-span-12 sm:col-span-5">
                <input
                  value={formatPhone(values.whatsapp)}
                  onChange={(e) => onChange({ whatsapp: e.target.value.replace(/\D/g, "").slice(0, 11) })}
                  placeholder="Só se não for o celular"
                  inputMode="tel"
                  className={inputClass}
                />
              </Field>
              )}
              {ver("contatoDeEmergencia") && (
              <>
              <Field icon={HeartHandshake} label={rotulo("Contato de emergência", "contatoDeEmergencia")} span="col-span-12 sm:col-span-7">
                <input
                  value={values.contatoDeEmergenciaNome}
                  onChange={(e) => onChange({ contatoDeEmergenciaNome: e.target.value })}
                  placeholder="Nome e parentesco"
                  maxLength={120}
                  className={inputClass}
                />
              </Field>
              <Field icon={Phone} label={rotulo("Telefone de emergência", "contatoDeEmergencia")} span="col-span-12 sm:col-span-5">
                <input
                  value={formatPhone(values.contatoDeEmergenciaTelefone)}
                  onChange={(e) => onChange({ contatoDeEmergenciaTelefone: e.target.value.replace(/\D/g, "").slice(0, 11) })}
                  placeholder="(11) 98765-4321"
                  inputMode="tel"
                  className={inputClass}
                />
              </Field>
              </>
              )}
            </div>
          </Section>
          )}

      {/* ─── Endereço (opcional) ────────────────────────────── */}
      {ver("endereco") && (
      <Section icon={MapPin} title={rotulo("Endereço", "endereco")} accent="#10b981">
        <div className="grid gap-2 sm:grid-cols-12">
          <Field icon={MapPinned} label="CEP" span="sm:col-span-4">
            <div className="flex items-center gap-1">
              <input
                value={formatCep(values.zipCode)}
                onChange={(e) => onChange({ zipCode: e.target.value.replace(/\D/g, "").slice(0, 8) })}
                placeholder="00000-000"
                inputMode="numeric"
                className={inputClass}
              />
              {cepStatus === "searching" && (
                <span className="shrink-0 text-[10px] text-zinc-400">buscando…</span>
              )}
              {cepStatus === "not_found" && (
                <span className="shrink-0 text-[10px] text-amber-600">não encontrado</span>
              )}
            </div>
          </Field>
          <Field icon={Home} label="Logradouro" span="sm:col-span-6">
            <input
              value={values.address}
              onChange={(e) => onChange({ address: e.target.value })}
              className={inputClass}
            />
          </Field>
          <Field label="Número" span="sm:col-span-2">
            <input
              value={values.addressNumber}
              onChange={(e) => onChange({ addressNumber: e.target.value })}
              inputMode="numeric"
              className={inputClass}
            />
          </Field>
          <Field label="Complemento" span="sm:col-span-6">
            <input
              value={values.addressComplement}
              onChange={(e) => onChange({ addressComplement: e.target.value })}
              placeholder="Apto, bloco..."
              className={inputClass}
            />
          </Field>
          <Field icon={MapPin} label="Bairro" span="sm:col-span-6">
            <input
              value={values.neighborhood}
              onChange={(e) => onChange({ neighborhood: e.target.value })}
              className={inputClass}
            />
          </Field>
          <Field icon={Building2} label="Cidade" span="sm:col-span-9">
            <input
              value={values.city}
              onChange={(e) => onChange({ city: e.target.value })}
              className={inputClass}
            />
          </Field>
          <Field icon={MapIcon} label="UF" span="sm:col-span-3">
            <select
              value={values.state}
              onChange={(e) => onChange({ state: e.target.value })}
              className={cn(inputClass, "cursor-pointer")}
            >
              <option value="">—</option>
              {BRAZILIAN_STATES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </Field>
        </div>
      </Section>
      )}

      {/* ─── Profissão e origem ─────────────────────────────── */}
      {(ver("profession") || ver("howDidMeet")) && (
      <Section icon={Briefcase} title="Profissão e origem" accent="#f59e0b">
        <div className="grid gap-2 sm:grid-cols-2">
          {ver("profession") && (
          <Field icon={Briefcase} label={rotulo("Profissão", "profession")}>
            <input
              value={values.profession}
              onChange={(e) => onChange({ profession: e.target.value })}
              placeholder="Ex.: Engenheira"
              className={inputClass}
            />
          </Field>
          )}
          {ver("howDidMeet") && (
          <Field icon={Sparkles} label={rotulo("Como conheceu", "howDidMeet")}>
            <select
              value={values.howDidMeet}
              onChange={(e) => onChange({ howDidMeet: e.target.value })}
              className={cn(inputClass, "cursor-pointer")}
            >
              <option value="">—</option>
              {HOW_DID_MEET_OPTIONS.map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </select>
          </Field>
          )}
        </div>
      </Section>
      )}

      {/* ─── Tags ───────────────────────────────────────────── */}
      <Section icon={Tags} title="Tags" accent="#ec4899">
        <PatientTagInput
          value={values.tags}
          onChange={(v) => onChange({ tags: v })}
          suggestions={tagsQuery.data ?? []}
          presets={TAG_PRESETS}
          placeholder="Ex.: VIP, alérgico, gestante..."
        />
      </Section>

          {/* ─── Observações (textarea) ─────────────────────── */}
          <Section icon={StickyNote} title="Observações" accent="#64748b">
            <textarea
              value={values.notes}
              onChange={(e) => onChange({ notes: e.target.value })}
              placeholder="Notas clínicas, preferências, histórico relevante..."
              rows={2}
              className={cn(
                "w-full resize-none rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm text-zinc-900 outline-none placeholder:text-zinc-400 transition-colors focus:border-brand-400 focus:ring-1 focus:ring-brand-400 dark:border-border dark:bg-card dark:text-foreground dark:placeholder:text-muted-foreground",
              )}
            />
          </Section>
        </>
      )}
    </div>
  );
}

// ─── Primitivas ──────────────────────────────────────────

const inputClass =
  "h-9 w-full rounded-md border border-zinc-200 bg-white px-2.5 text-sm text-zinc-900 outline-none placeholder:text-zinc-400 transition-colors focus:border-brand-400 focus:ring-1 focus:ring-brand-400 dark:border-border dark:bg-card dark:text-foreground dark:placeholder:text-muted-foreground";

function Section({
  icon: Icon,
  title,
  accent,
  children,
}: {
  icon: IconComp;
  title: string;
  accent: string;
  children: React.ReactNode;
}) {
  return (
    <section>
      <div className="mb-2 flex items-center gap-2">
        <span
          className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md"
          style={{ backgroundColor: `${accent}1a`, color: accent }}
        >
          <Icon className="h-3 w-3" />
        </span>
        <h4 className="text-[12px] font-bold tracking-tight text-zinc-900 dark:text-foreground">
          {title}
        </h4>
        <div className="h-px flex-1 bg-zinc-200 dark:bg-border" />
      </div>
      {children}
    </section>
  );
}

function Field({
  icon: Icon,
  label,
  hint,
  span,
  children,
}: {
  icon?: IconComp;
  label: string;
  /** Dica sutil em itálico à direita do label (ex: "formatação automática"). */
  hint?: string;
  span?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={span}>
      <div className="mb-0.5 flex items-center gap-1 px-0.5 text-[10px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-muted-foreground">
        {Icon && <Icon className="h-3 w-3 text-zinc-400 dark:text-muted-foreground" />}
        <span>{label}</span>
        {hint && (
          <span className="ml-1 hidden truncate text-[9.5px] font-normal normal-case italic tracking-normal text-zinc-400 sm:inline dark:text-muted-foreground/70">
            · {hint}
          </span>
        )}
      </div>
      {children}
    </div>
  );
}
